import React, { useState } from 'react';
import {
  Collapse,
  Typography,
  Row,
  Col,
  Card,
  Tabs,
  Checkbox,
  Input,
  Empty,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  defaultCategories,
  EventFormState,
  GenderKey,
  genderKeys,
  TournamentsByGender,
  TournamentsBySport,
} from '../../../../types/types';
import { SportLiteType } from '../../../../types/types';
import { TournamentType } from '../../../../enums/enums';
import styled from 'styled-components';
import Spinner from '../../../common/Spinner';
import { LoadingContainer } from './Step2Sports';
import { COLORS } from '../../../../utils/constants';
import Modal from '../../../common/Modal';
import Button from '../../../common/Button';
import { typeLabelMap, TYPES_BY_GENDER } from '../../../../utils/eventUtils';
import { useSnackbar } from '../../../../contexts/SnackbarContext/SnackbarProvider';

const { Panel } = Collapse;
const { Title, Text } = Typography;

interface Props {
  formState: EventFormState;
  setFormState: React.Dispatch<React.SetStateAction<EventFormState>>;
  errors?: Record<string, string>;
  sportsData: SportLiteType[];
  isSportsLoading: boolean;
}

const Step3Configure: React.FC<Props> = ({
  formState,
  setFormState,
  errors,
  sportsData,
  isSportsLoading,
}) => {
  const snackbar = useSnackbar();
  const [activeGenderBySport, setActiveGenderBySport] = useState<
    Record<number, GenderKey>
  >({});
  const [expandedCategoryBySport, setExpandedCategoryBySport] = useState<
    Record<number, string[]>
  >({});

  const [newCategoryModal, setNewCategoryModal] = useState<{
    open: boolean;
    sportId: number | null;
    categoryName: string;
  }>({ open: false, sportId: null, categoryName: '' });

  const [categoryError, setCategoryError] = useState<string>('');

  const categoriesBySport = formState.categoriesBySport || {};

  const getCategoriesForSport = (sportId: number): string[] =>
    categoriesBySport[sportId] || defaultCategories;

  const updateCategoriesForSport = (sportId: number, list: string[]) => {
    setFormState((prev) => ({
      ...prev,
      categoriesBySport: {
        ...(prev.categoriesBySport || {}),
        [sportId]: list,
      },
    }));
  };

  const addCategoryToSport = () => {
    if (!newCategoryModal.sportId) return;
    if (!newCategoryModal.categoryName.trim()) {
      setCategoryError('Please enter a category name.');
      return;
    }
    if (categoryError) setCategoryError('');
    const categoryName = newCategoryModal.categoryName.trim().toLowerCase();
    const categories =
      formState.categoriesBySport?.[newCategoryModal?.sportId || 0] || [];
    if (
      [...categories, ...defaultCategories].some(
        (c) => c.toLowerCase() === categoryName,
      )
    ) {
      snackbar.error('Category name already exists.');
      return;
    }
    const sportId = newCategoryModal.sportId;
    const current = getCategoriesForSport(sportId);
    const updated = [...current, newCategoryModal.categoryName.trim()];
    updateCategoriesForSport(sportId, updated);

    setNewCategoryModal({ open: false, sportId: null, categoryName: '' });
    snackbar.success('Categeory added successfully.');
  };

  const setActiveGender = (sportId: number, gender: GenderKey) => {
    setActiveGenderBySport((prev) => ({ ...prev, [sportId]: gender }));
  };

  const toggleCategoryExpansion = (sportId: number, category: string) => {
    setExpandedCategoryBySport((prev) => {
      const current = prev[sportId] || [];
      const updated = current.includes(category)
        ? current.filter((c) => c !== category)
        : [...current, category];
      return { ...prev, [sportId]: updated };
    });
  };

  const toggleTypeSelection = (
    sportId: number,
    gender: GenderKey,
    category: string,
    typeId: number,
  ) => {
    setFormState((prev: any) => {
      const prevTournaments = prev.tournaments || {};
      const newTournaments: Record<number, TournamentsBySport> = {
        ...prevTournaments,
      };

      if (!newTournaments[sportId]) {
        newTournaments[sportId] = { female: {}, male: {}, mixed: {} };
      }

      const sportObj = { ...newTournaments[sportId] };
      const genderObj: TournamentsByGender = { ...(sportObj[gender] || {}) };
      const current = Array.isArray(genderObj[category])
        ? [...genderObj[category]]
        : [];

      const updated = current.includes(typeId)
        ? current.filter((t) => t !== typeId)
        : [...current, typeId];

      genderObj[category] = updated;
      sportObj[gender] = genderObj;
      newTournaments[sportId] = sportObj;

      return { ...prev, tournaments: newTournaments };
    });
  };

  const isTypeSelected = (
    sportId: number,
    gender: GenderKey,
    category: string,
    typeId: number,
  ) => {
    const tournaments = formState.tournaments || {};
    const sportObj: any = tournaments[sportId];
    if (!sportObj) return false;
    const genderObj = sportObj[gender] || {};
    const arr = genderObj[category] || [];
    return Array.isArray(arr) && arr.includes(typeId);
  };

  const getSelectedCountForSport = (sportId: number) => {
    const tournaments = formState.tournaments || {};
    const sportObj: any = tournaments[sportId];
    if (!sportObj) return 0;
    let count = 0;
    genderKeys.forEach((g) => {
      const genderObj = sportObj[g] || {};
      Object.values(genderObj).forEach((arr) => {
        if (Array.isArray(arr)) count += arr.length;
      });
    });
    return count;
  };

  const handleCancelNewCategory = () => {
    setNewCategoryModal({ open: false, sportId: null, categoryName: '' });
    if (categoryError) setCategoryError('');
  };

  if (!formState.selectedSports?.length) {
    return (
      <Container>
        <Text>No sports selected.</Text>
        {errors?.selectedSports && (
          <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
            {errors.selectedSports}
          </Text>
        )}
      </Container>
    );
  }

  return (
    <Container>
      <TitleText level={4}>Configure Tournaments</TitleText>
      <TitleSubText type="secondary">
        Select gender, categories and types for each sport
      </TitleSubText>
      {errors?.tournaments && (
        <Text
          type="danger"
          style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}
        >
          {errors.tournaments}
        </Text>
      )}

      <Modal
        open={newCategoryModal.open}
        title="Add Category"
        minHeight={'auto'}
        onCancel={handleCancelNewCategory}
        removeFooterMargin
      >
        <StyledInput
          placeholder="Category name"
          value={newCategoryModal.categoryName}
          onChange={(e) => {
            setNewCategoryModal((p) => ({
              ...p,
              categoryName: e.target.value,
            }));
            if (categoryError) setCategoryError('');
          }}
        />
        {categoryError && (
          <CategoryErrorText type="danger">{categoryError}</CategoryErrorText>
        )}

        <ButtonSection>
          <Button onClick={handleCancelNewCategory} width={'auto'}>
            Cancel
          </Button>
          <Button type="primary" onClick={addCategoryToSport}>
            Add
          </Button>
        </ButtonSection>
      </Modal>

      {isSportsLoading && (
        <LoadingContainer>
          <Spinner />
          <Text>Loading sports...</Text>
        </LoadingContainer>
      )}
      {sportsData && sportsData.length === 0 && !isSportsLoading && <Empty />}

      {!isSportsLoading &&
        formState.selectedSports.map((sportId) => {
          const sport =
            sportsData?.find((s) => s.sportId === sportId) ??
            (null as SportLiteType | null);
          const isTeamSport = !!sport?.isTeamSport;
          const selectedCount = getSelectedCountForSport(sportId);
          const activeGender = activeGenderBySport[sportId] || 'female';
          const expandedCategories = expandedCategoryBySport[sportId] || [];
          const categories = getCategoriesForSport(sportId);

          return (
            <SportCard key={sportId}>
              <Collapse
                bordered={false}
                expandIconPosition="end"
                defaultActiveKey={[]}
              >
                <Panel
                  key={sportId}
                  header={
                    <Row align="middle" gutter={12}>
                      <Col>
                        <SportHeaderImageWrapper>
                          {sport?.sportImageUrl ? (
                            <SportHeaderImage
                              src={sport.sportImageUrl}
                              alt={sport.sportName}
                            />
                          ) : (
                            <Text type="secondary">No image</Text>
                          )}
                        </SportHeaderImageWrapper>
                      </Col>
                      <Col flex="auto">
                        <div>
                          <Text strong>
                            {sport?.sportName ?? `Sport ${sportId}`}
                          </Text>
                          <br />
                          <Text type="secondary">{selectedCount} selected</Text>
                        </div>
                      </Col>
                    </Row>
                  }
                >
                  <SportPanelBody>
                    <Tabs
                      activeKey={activeGender}
                      onChange={(key) =>
                        setActiveGender(sportId, key as GenderKey)
                      }
                      items={genderKeys.map((g) => ({
                        key: g,
                        label: g.charAt(0).toUpperCase() + g.slice(1),
                      }))}
                    />

                    {categories.map((cat) => {
                      const isExpanded = expandedCategories.includes(cat);
                      const allowedTypes = isTeamSport
                        ? [TournamentType.Team]
                        : TYPES_BY_GENDER[activeGender];

                      return (
                        <CategoryCard key={cat} size="small">
                          <Collapse
                            bordered={false}
                            activeKey={isExpanded ? [cat] : []}
                            onChange={() =>
                              toggleCategoryExpansion(sportId, cat)
                            }
                          >
                            <Panel
                              key={cat}
                              header={
                                <CategoryHeader>
                                  <Text>{cat}</Text>
                                </CategoryHeader>
                              }
                              showArrow
                            >
                              <CategoryPanelBody>
                                {allowedTypes.map((typeId) => {
                                  const selected = isTypeSelected(
                                    sportId,
                                    activeGender as GenderKey,
                                    cat,
                                    typeId,
                                  );
                                  return (
                                    <TypeRow as="label" key={typeId}>
                                      <Checkbox
                                        checked={selected}
                                        onChange={() =>
                                          toggleTypeSelection(
                                            sportId,
                                            activeGender as GenderKey,
                                            cat,
                                            typeId,
                                          )
                                        }
                                      />
                                      <Text>
                                        {typeLabelMap[typeId] || 'Type'}
                                      </Text>
                                    </TypeRow>
                                  );
                                })}
                              </CategoryPanelBody>
                            </Panel>
                          </Collapse>
                        </CategoryCard>
                      );
                    })}

                    <Button
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        setNewCategoryModal({
                          open: true,
                          sportId,
                          categoryName: '',
                        })
                      }
                    >
                      Add category
                    </Button>
                  </SportPanelBody>
                </Panel>
              </Collapse>
            </SportCard>
          );
        })}
    </Container>
  );
};

export const Container = styled.div`
  padding: 24px;
`;

export const PageTitle = styled.h4`
  text-align: center;
  margin-bottom: 4px;
`;

export const Subtitle = styled.span`
  display: block;
  text-align: center;
  margin-bottom: 20px;
`;

export const SportCard = styled(Card)`
  margin-bottom: 16px;

  .ant-card-body {
    padding: 0;
  }
`;

export const SportHeaderImageWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  border: 1px solid ${COLORS.grey.secondaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

export const SportHeaderImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

export const SportPanelBody = styled.div`
  padding: 16px;
`;

export const CategoryCard = styled(Card)`
  margin-bottom: 8px;

  .ant-card-body {
    padding: 0;
  }
`;

export const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const CategoryPanelBody = styled.div`
  padding: 8px 16px;
  background: ${COLORS.grey.lightMedium};
  border-radius: 8px;
`;

export const TypeRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
`;

const TitleText = styled(Title)`
  text-align: center;
  margin-bottom: 4px;
`;

const TitleSubText = styled(Text)`
  display: block;
  text-align: center;
  margin-bottom: 20px;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
`;

const StyledInput = styled(Input)`
  height: 43px;
  input {
    height: 43px;
  }
`;

const CategoryErrorText = styled(Text)`
  display: block;
  margin-top: 8px;
`;

export default Step3Configure;
